import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { createApolloClient, createApolloCache } from 'providers/ApolloProvider';
import {
  QUERY_assignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderId,
  QUERY_assignToAgencyTeamMemberFetchAgencyJaneById,
  QUERY_assignToAgencyTeamMemberFetchAgencyJanes,
  QUERY_assignToAgencyTeamMemberFetchAgencyJanesCount,
} from 'queries';
import {
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQueryVariables,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQueryVariables,
  AssignToAgencyTeamMemberFetchAgencyJanesCountQuery,
  AssignToAgencyTeamMemberFetchAgencyJanesCountQueryVariables,
} from 'types/graphql';

const agencyJaneOrderValidationClient = createApolloClient({ cache: createApolloCache() });
export const agencyJaneOrderValidationQueryConfig = ({
  janeId,
  orderId,
  orderRelayId,
  breakTime,
  onSuccess,
}: {
  janeId: string;
  orderId: number;
  orderRelayId: string;
  breakTime?: string;
  onSuccess: (
    data: AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  ) => void;
}): UseGQLQueryConfig<
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQueryVariables
> => ({
  key: 'FETCH_AGENCY_TEAM_MEMBER_AVAILABILITY_BY_ORDER_ID',
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderId,
  client: agencyJaneOrderValidationClient,
  onSuccess,
  variables: {
    janeId,
    orderId,
    orderRelayId,
    breakTime,
  },
});

const fetchAgencyJaneClient = createApolloClient({ cache: createApolloCache() });
export const fetchAgencyJaneQueryConfig = ({
  janeId,
  onSuccess,
}: {
  janeId: string;
  onSuccess: (data: AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery) => void;
}): UseGQLQueryConfig<
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQueryVariables
> => ({
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJaneById,
  key: 'ASSIGN_TO_AGENCY_TEAM_MEMBER_FETCH_AGENCY_JANE_BY_ID',
  client: fetchAgencyJaneClient,
  onSuccess,
  variables: {
    janeId,
  },
});

const fetchAgencyTeamMembersClient = createApolloClient({ cache: createApolloCache() });
export const fetchAgencyTeamMembersQueryConfig = ({
  orderId,
  positionId,
  agencyId,
  agencyCommunityId,
  breakTime,
}: {
  orderId: string;
  positionId: number;
  agencyId: number;
  agencyCommunityId: number[];
  breakTime?: string;
}): ISSFBehaviorsOptions => ({
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJanes,
  key: 'ASSIGN_TO_AGENCY_TEAM_MEMBER_FETCH_AGENCY_JANES',
  paginationType: 'InfiniteScroll',
  pageSize: 12,
  client: fetchAgencyTeamMembersClient,
  schema: {
    orderId: {
      initialValue: orderId,
    },
    byAgency: {
      initialValue: agencyId,
    },
    byFullName: {
      initialValue: '',
    },
    byPositionId: {
      initialValue: positionId,
    },
    byAgencyCommunityId: {
      initialValue: agencyCommunityId,
    },
    breakTime: {
      initialValue: breakTime,
    },
  },
});

export const fetchAgencyTeamMembersCountQueryConfig = ({
  orderId,
  agencyCommunityId,
  breakTime,
}: {
  orderId: string;
  agencyCommunityId: number[];
  breakTime?: string;
}): UseGQLQueryConfig<
  AssignToAgencyTeamMemberFetchAgencyJanesCountQuery,
  { totalCount: number },
  AssignToAgencyTeamMemberFetchAgencyJanesCountQueryVariables
> => ({
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJanesCount,
  key: 'ASSIGN_TO_AGENCY_TEAM_MEMBER_FETCH_AGENCY_JANES_COUNT',
  variables: {
    orderId: orderId,
    byAgencyCommunityId: agencyCommunityId,
    breakTime,
  },
  responseTransformer: (response) => ({
    totalCount: response?.requestJanes?.totalCount,
  }),
});
